import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { getCustomizedFileBlobApi, getEndUserFilesApi } from '../../services/axios/AuthApis/TrustCenterApis';
import DocumentPreviewModal from './DocumentPreviewModal';
import ApiLoader from '../ui/ApiLoader'; // optional, for consistency
import { DataTable } from '../common/DataTable';

const MyLibrariesView = ({ customerId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_pages: 1, count: 0 });
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // downloaded file (Blob) shown in the preview
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewScrollCompleted, setPreviewScrollCompleted] = useState(false);
  const [previewDocMeta, setPreviewDocMeta] = useState(null);
  const [previewPageCount, setPreviewPageCount] = useState(1);
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const previewScrollRef = React.useRef(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Rename from pendingFiles to uploadedFiles since these are approved/uploaded files
  const [selectedRecord, setSelectedRecord] = useState(null);
  // Fetch data from backend
  const fetchFiles = useCallback(async (page = 1, pageSizeArg = pageSize) => {
    if (!customerId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getEndUserFilesApi(page, pageSizeArg, {
        customer_id: customerId,
        search,
        framework_id: frameworkFilter,
        category: categoryFilter,
      });
      // Expecting { success: true, files: [...] } from the API
      setRecords(data.files || []);
      setPagination({
        page: data.page || 1,
        page_size: pageSizeArg,
        total_pages: data.total_pages || 1,
        count: data.count || 0,
      });
    } catch (error) {
      console.error('Failed to load user files:', error);
      setRecords([]);
      setPagination({ page: 1, page_size: pageSizeArg, total_pages: 1, count: 0 });
    } finally {
      setLoading(false);
    }
  }, [customerId, search, frameworkFilter, categoryFilter, pageSize]);

  useEffect(() => {
    fetchFiles(pagination.page, pageSize);
  }, [fetchFiles, pagination.page, pageSize]);

  const getUploadedFiles = (record) => {
    const files = [];
    (record.controls || []).forEach((control) => {
      // Approved customized files
      (control.customized_document_file || []).forEach((file) => {
        files.push({
          ...file,
          source: 'customized',
          control_id: control.control_id,
          control_code: control.control_code,
          control_name: control.control_name,
          document_id: record._id || record.id,
        });
      });
      // Auditor response files
      (control.auditor_files || []).forEach((auditorFile) => {
        files.push({
          ...auditorFile,
          source: 'auditor',
          control_id: control.control_id,
          control_code: control.control_code,
          control_name: control.control_name,
          document_id: record._id || record.id,
          file_name: auditorFile.file_name,
          file_url: auditorFile.file_url,
          file_id: auditorFile.file_id,
          uploaded_by: auditorFile.uploaded_by,
          uploaded_at: auditorFile.uploaded_at,
        });
      });
    });
    return files;
  };

  // Format framework names for display
  const formatFramework = (record) => {
    const names = record.framework_names || [];
    if (names.length > 0) return names.join(', ');
    // Fallback: framework_details array
    if (record.framework_details?.length) {
      return record.framework_details.map(fw => fw.name).join(', ');
    }
    return '—';
  };

  // Preview handler – loads uploaded files for a document
  const handleViewFile = async (record) => {
    const allFiles = getUploadedFiles(record);
    if (allFiles.length === 0) return;

    setUploadedFiles(allFiles);
    setSelectedRecord(record);
    setCurrentFileIndex(0);

    const file = allFiles[0];
    setPreviewTitle(file.file_name || file.file?.split('/').pop() || 'File Preview');
    setPreviewLoading(true);
    setShowPreviewModal(true);
    try {
      let url;
      
        const payload = {
          document_id: file.document_id,
          control_id: file.control_id,
          file_id: file.file_id,
          file_type: file.source,
        };
        // Streamed through the backend and shown from memory (S3 links are blocked in the app).
      setPreviewFile(await getCustomizedFileBlobApi(payload.document_id, payload.control_id, payload.file_id, payload.file_type));
      setPreviewUrl('');
    } catch (err) {
      console.error(err);
      setPreviewUrl('');
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle file navigation when user clicks prev/next
  useEffect(() => {
    if (uploadedFiles.length > 0 && currentFileIndex >= 0 && currentFileIndex < uploadedFiles.length) {
      const file = uploadedFiles[currentFileIndex];
      setPreviewTitle(file.file_name || file.file?.split('/').pop() || 'File Preview');
      setPreviewLoading(true);

      const loadFile = async () => {
        try {
          let url;
            const payload = {
              document_id: file.document_id,
              control_id: file.control_id,
              file_id: file.file_id,
              file_type: file.source,
            }
            // Streamed through the backend and shown from memory (S3 links are blocked in the app).
          setPreviewFile(await getCustomizedFileBlobApi(payload.document_id, payload.control_id, payload.file_id, payload.file_type));
          setPreviewUrl('');
        } catch (err) {
          console.error(err);
          setPreviewUrl('');
          setPreviewFile(null);
        } finally {
          setPreviewLoading(false);
        }
      };

      loadFile();
    }
  }, [currentFileIndex, uploadedFiles]);

  // Helper to get distinct categories for filter dropdown
  const uniqueCategories = [...new Set(records.map(r => r.category).filter(Boolean))];

  const columns = [
    {
      id: "index",
      header: "#",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-gray-500">{(pagination.page - 1) * pagination.page_size + row.index + 1}</span>
      ),
    },
    {
      id: "title",
      header: "Title",
      cell: ({ row }) => (
        <span className="font-medium text-gray-900">{row.original.policy_procedure_guideline || 'Untitled document'}</span>
      ),
    },
    {
      id: "framework",
      header: "Framework",
      cell: ({ row }) => <span className="text-gray-600">{formatFramework(row.original)}</span>,
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ getValue }) => <span className="text-gray-600">{getValue() || '—'}</span>,
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ getValue }) => <span className="text-gray-600">{getValue() || '—'}</span>,
    },
    {
      id: "files",
      header: "Files",
      enableSorting: false,
      cell: ({ row }) => {
        const files = getUploadedFiles(row.original);
        return files.length === 0 ? (
          <span className="text-gray-400">None</span>
        ) : (
          <button
            onClick={() => handleViewFile(row.original)}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium"
            title="View files"
          >
            <EyeIcon className="w-3 h-3" />
            {files.length} file{files.length > 1 ? 's' : ''}
          </button>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      cell: ({ row }) => {
        const files = getUploadedFiles(row.original);
        return files.some((f) => f.status && f.status !== 'approved') ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700">
            Pending Approval
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
            Approved
          </span>
        );
      },
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header and filters - same as approval view */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">My Uploaded Files</h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            Approved customized documents you have uploaded.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row items-stretch sm:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="Search by title, category…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="relative min-w-[180px]">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={frameworkFilter}
              onChange={(e) => {
                setFrameworkFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Frameworks</option>
              {/* frameworks prop may be passed from parent; if not, extract from records */}
              {Array.from(new Set(records.flatMap(r => r.framework_names || []))).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="relative min-w-[180px]">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={records}
        getRowId={(record) => String(record._id || record.id)}
        loading={loading}
        emptyMessage="No approved customized files found."
        manualPagination
        rowCount={pagination.count || 0}
        pagination={{ pageIndex: (pagination.page || 1) - 1, pageSize: pagination.page_size }}
        onPaginationChange={(updater) => {
          const current = { pageIndex: (pagination.page || 1) - 1, pageSize: pagination.page_size };
          const next = typeof updater === "function" ? updater(current) : updater;
          if (next.pageSize !== pageSize) {
            setPageSize(next.pageSize);
            setPagination((prev) => ({ ...prev, page: 1, page_size: next.pageSize }));
          } else {
            setPagination((prev) => ({ ...prev, page: next.pageIndex + 1 }));
          }
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        maxBodyHeight="65vh"
      />

      {/* Preview Modal (no approve/reject) */}
      <DocumentPreviewModal
        showDocumentPreviewModal={showPreviewModal}
        setShowDocumentPreviewModal={setShowPreviewModal}
        documentPreviewTitle={previewTitle}
        setDocumentPreviewTitle={setPreviewTitle}
        documentPreviewLoading={previewLoading}
        setDocumentPreviewLoading={setPreviewLoading}
        documentPreviewUrl={previewUrl}
        documentPreviewFile={previewFile}
        setDocumentPreviewUrl={setPreviewUrl}
        previewScrollContainerRef={previewScrollRef}
        handlePreviewScroll={() => {}}
        isPreviewScrollCompleted={previewScrollCompleted}
        setIsPreviewScrollCompleted={setPreviewScrollCompleted}
        handleAgreeDocument={() => {}}
        previewDocumentMeta={previewDocMeta}
        setPreviewDocumentMeta={setPreviewDocMeta}
        previewPageCount={previewPageCount}
        setPreviewPageCount={setPreviewPageCount}
        previewCurrentPage={previewCurrentPage}
        setPreviewCurrentPage={setPreviewCurrentPage}
        user={null}
        showIAgree={false}
        showApproveReject={false}
        pendingFiles={uploadedFiles}
        currentFileIndex={currentFileIndex}
        setCurrentFileIndex={setCurrentFileIndex}
      />
    </motion.div>
  );
};

export default MyLibrariesView;